import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import startCase from 'lodash/startCase';
import React, { useEffect, useState, useRef } from 'react';
import useProject from 'src/shared/hooks/useProject';
import useProjectBreadcrumb from 'src/shared/hooks/useProjectBreadcrumb';
import { useSpeciesTrendsQuery } from 'src/app/ecology-hub/state/api/ecologyHubGraphSlice';
import { transformData } from 'src/app/ecology-hub/views/species-trends/SpeciesTrends.utils';
import { AppName } from 'src/global/globalConfig';
import TimeSeriesReChart from 'src/shared/components/charts/time-series/TimeSeriesReChart';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import HabitatAssayLozenges from 'src/shared/components/lozenges/HabitatAssayLozenges';
import Select, { SelectOption } from 'src/shared/components/select/Select';
import useProjectFilters from 'src/shared/hooks/useProjectFilters';
import { useAppDispatch, useAppSelector } from 'src/store';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import { chartExplainerSlugKey } from 'src/shared/constants/chartExplainerKeys';
import { InputLabel } from 'src/shared/components/ui/input-label';
import { ChartIds } from 'src/shared/types';
import Show from 'src/shared/components/show/Show';
import DownloadImageLogo from 'src/shared/components/download-image-logo/DownloadImageLogo';
import DemoProjectBanner from 'src/shared/components/demo-project-banner/DemoProjectBanner';
import SpeciesTrendsDownloadImageButton from './components/species-trends-download-image-button/SpeciesTrendsDownloadImageButton';
import styles from './SpeciesTrends.module.scss';
import { updateFilters } from '../../state/species-trends/speciesTrendsSlice';
import { getCurrentSpeciesTrendsUIState } from '../../state/species-trends/speciesTrendsSelector';

const PAGE_TITLE = 'Species Trends';

const mspiFooter = (
    <div className='flex flex-col ml-10 gap-4 font-medium'>
        <span>
            <span className='inline-block w-[170px]'>Unpolluted :</span> 0 to 1.2
        </span>
        <span>
            <span className='inline-block w-[170px]'>Slightly polluted :</span> 1.3 to 3.3
        </span>
        <span>
            <span className='inline-block w-[170px]'>Moderately polluted :</span> 3.4 to 5
        </span>
        <span>
            <span className='inline-block w-[170px]'>Heavily polluted :</span> 5.1 to 6
        </span>
        <span>
            <span className='inline-block w-[170px]'>Extremely polluted :</span> 6.1 to 7
        </span>
    </div>
);

const SpeciesTrends = () => {
    const chartRef = useRef<HTMLDivElement>(null);
    const { isStarterProject } = useProject();
    const {
        currentProjectId,
        currentProjectFilters,
        currentProjectSelectedHabitatAssayType: habitatAssayType,
        setCurrentProjectHabitatAssayType,
        getColourForArea,
    } = useProjectFilters();
    const currentProjectBreadcrumbDetails = useProjectBreadcrumb(PAGE_TITLE);

    const dispatch = useAppDispatch();

    const [metricOptions, setMetricOptions] = useState<SelectOption[]>([
        {
            label: 'None available',
            value: 'none',
        },
    ]);

    const uiState = useAppSelector(getCurrentSpeciesTrendsUIState);

    const {
        filters: { chartId },
    } = uiState;

    useEffect(() => {
        if (!currentProjectFilters || !habitatAssayType) {
            return;
        }

        const { habitatAssays } = currentProjectFilters;

        const newMetricsOptions = habitatAssays
            .find(entry => entry.key === habitatAssayType?.key)
            ?.metrics.map(metric => ({
                label: startCase(metric.metricName),
                value: metric.metricKey,
            })) as SelectOption[];

        setMetricOptions(newMetricsOptions);
        if (!chartId) {
            dispatch(
                updateFilters({
                    chartId: newMetricsOptions[0].value,
                })
            );
        }
    }, [currentProjectFilters?.habitatAssays, habitatAssayType, chartId]);

    const { currentData, isFetching } = useSpeciesTrendsQuery(
        {
            projectId: currentProjectId || '',
            chartId,
            habitatAssay: habitatAssayType?.key || '',
        },
        {
            skip: !currentProjectId || !habitatAssayType || !chartId,
        }
    );

    if (!currentData || isFetching || !currentProjectId) {
        return (
            <>
                <Loader />
            </>
        );
    }

    const { speciesTrends: speciesRichnessEcologyHubData } = currentData;

    const timeSeriesData = speciesRichnessEcologyHubData && transformData(speciesRichnessEcologyHubData.data[0].data, getColourForArea);

    const onMetricChange = (value: string) => {
        dispatch(
            updateFilters({
                chartId: value,
            })
        );
    };

    const otherActions = (
        <div className='flex flex-col gap-6 items-end'>
            <div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <InputLabel style={{ marginRight: '8x' }}>Metric</InputLabel>
                    <QuestionButtonHelp type='api' placement='right' slug='metrics-explainer' detailedExplainerSlug='species-trends-te' />
                </div>
                <Select options={metricOptions} onChange={onMetricChange} selectedValue={chartId} width={280} testId='metric-dropdown' />
            </div>

            {/* Display Download image button non-starter subscription */}
            <Show when={!isStarterProject && !isEmpty(speciesRichnessEcologyHubData)}>
                <div className='w-[1px] h-10 bg-grey-20'>&nbsp;</div>
                <SpeciesTrendsDownloadImageButton chartContainer={chartRef} />
            </Show>
        </div>
    );

    const selectedHabitat = currentProjectFilters?.habitatAssayTypeOptions?.find(option => option.value === habitatAssayType?.key);

    const mainContent = (
        <div className='p-6 bg-white rounded-lg shadow-sm relative' ref={chartRef}>
            {/* NM logo for image downaload */}
            <DownloadImageLogo
                containerClass='absolute right-[7%] top-6 hidden'
                textClass='absolute top-[-5px] right-0 text-[17px] text-grey-80'
            />

            <div className={styles.chartTitle}>
                <div className={styles.titleValue}>{find(metricOptions, { value: chartId })?.label}</div>
                {chartExplainerSlugKey[chartId] && (
                    <QuestionButtonHelp type='api' placement='right' slug={chartExplainerSlugKey[chartId]} />
                )}
            </div>

            {/* Hidden subtitle for image download */}
            <div className='text-primary text-sm font-bold hidden' id='data-chart-subtitle'>
                {`${selectedHabitat?.habitatName} ${selectedHabitat?.assayName}`}
            </div>

            {!isEmpty(speciesRichnessEcologyHubData) ? (
                <TimeSeriesReChart chartData={timeSeriesData} />
            ) : (
                <div className='text-lg'>No Metrics to display</div>
            )}

            {ChartIds.MARINE_SEDIMENT_POLLUTION_INDEX === chartId && mspiFooter}
        </div>
    );

    const onHabitatAssayTypeChange = (value: string) => {
        onMetricChange('');
        setCurrentProjectHabitatAssayType(value);
    };

    const lozenges = (
        <HabitatAssayLozenges
            options={currentProjectFilters?.habitatAssayTypeOptions || []}
            selectedValue={habitatAssayType?.key || ''}
            onChange={onHabitatAssayTypeChange}
            app={AppName.SPECIES_TRENDS}
        />
    );

    return (
        <StandardAppLayout
            lozenges={lozenges}
            mainContent={mainContent}
            otherActions={otherActions}
            breadcrumbs={currentProjectBreadcrumbDetails}
            title={PAGE_TITLE}
            subTitle='Time series by sample group'
            topBanner={<DemoProjectBanner pageName={AppName.SPECIES_TRENDS} />}
        />
    );
};

export default SpeciesTrends;
